import type { FloorBeat, FloorCell, FloorStation } from '../api'

/**
 * The track — the Floor's animated figure.
 *
 * ## Why this one moves when the other consoles deliberately do not
 *
 * The dossier's plate and the polling studio's figures hold still on purpose: a record and an
 * estimate are not going anywhere, and motion on them would be decoration pretending to be
 * information. This is the opposite case. Polling day is the one surface in the platform where
 * the subject genuinely moves, where the useful question is a RATE, and where a still frame
 * hides the only thing worth knowing — whether a cell is climbing or has stopped.
 *
 * So every mark here is animated, and each animation carries a fact:
 *
 * - **Position eases** toward its target instead of jumping, so a cell that gains four voters
 *   between refreshes is seen to move rather than found somewhere new.
 * - **Trails** are the last few positions, so direction and speed are visible in a single
 *   frame. A cell with no trail has not moved; a long trail is a cell being worked.
 * - **Arrival rings** pulse once when a cell's count rises. That is the heartbeat of the floor,
 *   and it is the difference between a quiet board and a dead one.
 * - **Stalled cells breathe** — a slow dim pulse for a cell nobody has reported on. It is the
 *   only animation here that means "nothing is happening", and it has to look different from
 *   the ones that mean the opposite.
 *
 * ## The pace line is the whole judgement
 *
 * One vertical rule at the share of the day that has elapsed. A cell to its left is behind; a
 * cell to its right is ahead. It sweeps right as the day goes, so a cell standing still is
 * overtaken by it — which is exactly what happens in the field, and needs no legend.
 */

export interface Rgb { r: number, g: number, b: number }

export interface Theme {
  floor: Rgb
  floorEdge: Rgb
  ink: Rgb
  inkDim: Rgb
  inkFaint: Rgb
  grid: Rgb
  /** Ahead of pace, or done. */
  ahead: Rgb
  /** Behind pace. */
  behind: Rgb
  /** Badly behind, or an escalation past its due time. */
  critical: Rgb
  /** In the queue — moving, but not counted yet. */
  queue: Rgb
  /** The pace rule and the clock. */
  pace: Rgb
}

const FALLBACK: Theme = {
  floor: { r: 14, g: 17, b: 24 },
  floorEdge: { r: 22, g: 27, b: 37 },
  ink: { r: 231, g: 238, b: 247 },
  inkDim: { r: 148, g: 161, b: 180 },
  inkFaint: { r: 96, g: 108, b: 128 },
  grid: { r: 40, g: 48, b: 62 },
  ahead: { r: 45, g: 226, b: 178 },
  behind: { r: 255, g: 176, b: 46 },
  critical: { r: 255, g: 84, b: 84 },
  queue: { r: 96, g: 178, b: 255 },
  pace: { r: 226, g: 232, b: 240 },
}

export function readTheme(el: HTMLElement): Theme {
  const cs = getComputedStyle(el)
  const probe = document.createElement('canvas')
  probe.width = 1
  probe.height = 1
  const ctx = probe.getContext('2d', { willReadFrequently: true })
  const token = (name: string, fallback: Rgb): Rgb => {
    const value = cs.getPropertyValue(name).trim()
    if (!ctx || !value) return fallback
    ctx.fillStyle = '#000'
    ctx.fillStyle = value
    ctx.fillRect(0, 0, 1, 1)
    try {
      const d = ctx.getImageData(0, 0, 1, 1).data
      return { r: d[0] ?? 0, g: d[1] ?? 0, b: d[2] ?? 0 }
    } catch {
      return fallback
    }
  }
  return {
    floor: token('--floor-bg', FALLBACK.floor),
    floorEdge: token('--floor-bg-edge', FALLBACK.floorEdge),
    ink: token('--floor-ink', FALLBACK.ink),
    inkDim: token('--floor-ink-dim', FALLBACK.inkDim),
    inkFaint: token('--floor-ink-faint', FALLBACK.inkFaint),
    grid: token('--floor-grid', FALLBACK.grid),
    ahead: token('--floor-ahead', FALLBACK.ahead),
    behind: token('--floor-behind', FALLBACK.behind),
    critical: token('--floor-critical', FALLBACK.critical),
    queue: token('--floor-queue', FALLBACK.queue),
    pace: token('--floor-pace', FALLBACK.pace),
  }
}

export const rgba = (c: Rgb, a: number) => `rgba(${c.r},${c.g},${c.b},${a})`

export const TRACK = { left: 148, right: 28, top: 30, bottom: 26 }

/** How many past positions a comet keeps. Short enough to read as motion, not as a smear. */
const TRAIL = 14

/**
 * A cell's live drawing state.
 *
 * Held outside React on purpose: this mutates sixty times a second, and a state update per
 * particle per frame would re-render the page instead of the canvas.
 */
export interface Mote {
  id: string
  /** Eased screen position. */
  x: number
  y: number
  /** Where it is heading. */
  tx: number
  ty: number
  trail: number[]
  /**
   * The pass that last touched this mote. Pruning compares it against the current pass
   * instead of searching the scene — see the note in {@link step}.
   */
  seen: number
  /** Counts down after an arrival, driving the ring. */
  flash: number
  lastVoted: number
  radius: number
}

export interface TrackScene {
  cells: FloorCell[]
  stations: FloorStation[]
  /** Cumulative voted per cell at the scrubbed hour, by cell id. */
  votedAt: Map<string, number>
  /** 0..1 — where the pace rule sits. */
  paceShare: number
  width: number
  height: number
  dpr: number
  theme: Theme
  now: number
  hovered: string | null
  selected: string | null
  /** Cell ids the filter did not match. Null when nothing is filtering. */
  missed: Set<string> | null
  showTrails: boolean
  /** Set while the day is replaying — the pace rule leads rather than trails. */
  replaying: boolean
}

/**
 * Lane geometry.
 *
 * One lane per station, cells packed inside it by index. Stations rather than a free beeswarm
 * because a van is dispatched to a station: the reader's next action is scoped to the lane, so
 * the lane is what the figure should make easy to compare.
 */
export function laneOf(scene: TrackScene, cell: FloorCell): { top: number, height: number } {
  const lanes = Math.max(1, scene.stations.length)
  const plotH = scene.height - TRACK.top - TRACK.bottom
  const height = plotH / lanes
  const index = Math.max(0, scene.stations.findIndex((s) => s.id === cell.stationId))
  return { top: TRACK.top + index * height, height }
}

/** Deterministic vertical offset inside a lane. Never `Math.random` — a cell must not jitter. */
function seatOf(id: string): number {
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) / 4294967296
}

export function targetOf(scene: TrackScene, cell: FloorCell): { x: number, y: number } {
  const plotW = scene.width - TRACK.left - TRACK.right
  const voted = scene.votedAt.get(cell.id) ?? cell.voted
  const share = cell.members === 0 ? 0 : Math.min(1, voted / cell.members)
  const lane = laneOf(scene, cell)
  return {
    x: TRACK.left + share * plotW,
    y: lane.top + lane.height * (0.18 + seatOf(cell.id) * 0.64),
  }
}

/** Radius by outstanding members: the bigger the dot, the more people are still out. */
export function radiusOf(cell: FloorCell, votedAt: number): number {
  const outstanding = Math.max(0, cell.members - votedAt)
  return 3 + Math.sqrt(outstanding) * 1.9
}

/**
 * Ink for a cell.
 *
 * Green once it is done or ahead of pace, amber when behind, red when badly behind AND there is
 * still a day left to fix it. The last condition matters: after the close, "badly behind" is a
 * result rather than an emergency, and a board still screaming at 6pm is a board nobody reads
 * the next time.
 */
export function inkOf(scene: TrackScene, cell: FloorCell, votedAt: number): Rgb {
  const T = scene.theme
  const share = cell.members === 0 ? 0 : votedAt / cell.members
  const behind = scene.paceShare - share
  if (share >= 0.999) return T.ahead
  if (behind <= 0.02) return T.ahead
  if (behind > 0.22 && cell.perHourNeeded !== null) return T.critical
  return T.behind
}

/**
 * Advance the simulation one frame.
 *
 * Returns true while anything is still moving, so the caller can idle a settled board instead
 * of burning a core on a still picture. Idle does not mean frozen — the arrival rings and the
 * stalled pulse keep it alive — but it does mean the eased positions have arrived.
 */
/**
 * Which pass of {@link step} we are on. Bumped once per frame and stamped onto every mote
 * the scene still contains, so the prune below is a comparison rather than a search.
 */
let pass = 0

export function step(motes: Map<string, Mote>, scene: TrackScene, dt: number): boolean {
  let moving = false
  pass++
  for (const cell of scene.cells) {
    const target = targetOf(scene, cell)
    const votedAt = scene.votedAt.get(cell.id) ?? cell.voted
    let mote = motes.get(cell.id)
    if (!mote) {
      // A cell appearing for the first time starts AT its target rather than flying in from
      // the origin: the flight would be a piece of motion that means nothing.
      mote = { id: cell.id, x: target.x, y: target.y, tx: target.x, ty: target.y,
        trail: [], flash: 0, lastVoted: votedAt, radius: radiusOf(cell, votedAt), seen: pass }
      motes.set(cell.id, mote)
    }
    mote.seen = pass
    mote.tx = target.x
    mote.ty = target.y
    mote.radius += (radiusOf(cell, votedAt) - mote.radius) * 0.18

    if (votedAt > mote.lastVoted) mote.flash = 1
    mote.lastVoted = votedAt

    const ease = 1 - Math.pow(0.001, dt)
    mote.x += (mote.tx - mote.x) * ease
    mote.y += (mote.ty - mote.y) * ease
    if (Math.abs(mote.tx - mote.x) > 0.4 || Math.abs(mote.ty - mote.y) > 0.4) moving = true

    mote.trail.push(mote.x, mote.y)
    if (mote.trail.length > TRAIL * 2) mote.trail.splice(0, mote.trail.length - TRAIL * 2)
    if (mote.flash > 0) {
      mote.flash = Math.max(0, mote.flash - dt * 1.4)
      moving = true
    }
  }
  /*
     A cell that left the payload leaves the board, rather than sitting there for ever as a
     ghost of a scope the reader has already changed.

     THIS USED TO BE QUADRATIC, AND IT IS WHY THE BOARD HUNG.

     It was `motes.keys()` copied into an array, and for each key a `scene.cells.some(...)`
     linear search — n² comparisons, sixty times a second, plus two array allocations a
     frame. A ward's forty cells cost 1,600 comparisons and nobody noticed. A county's two
     thousand cells cost four million per frame, which is a quarter of a billion a second:
     the frame budget is gone long before anything is drawn, the tab stops responding, and it
     looks exactly like "the replay hangs when there is too much data" — because the replay
     is the only thing that pushes the board to a full day of cells at once.

     Each mote now carries the pass that touched it. Cells still in the scene were stamped in
     the loop above, so anything with an older stamp is gone — one comparison each, no
     search, no allocation. Deleting from a Map while iterating it is defined behaviour: an
     entry removed before it is reached is simply never visited.
  */
  for (const [id, mote] of motes) {
    if (mote.seen !== pass) motes.delete(id)
  }
  return moving
}

export function drawTrack(
  ctx: CanvasRenderingContext2D, scene: TrackScene, motes: Map<string, Mote>,
) {
  const { theme: T, width: W, height: H, dpr } = scene
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)

  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, rgba(T.floorEdge, 1))
  g.addColorStop(1, rgba(T.floor, 1))
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  drawLanes(ctx, scene)
  drawGrid(ctx, scene)
  drawPace(ctx, scene)
  drawMotes(ctx, scene, motes)
}

function drawLanes(ctx: CanvasRenderingContext2D, scene: TrackScene) {
  const { theme: T, width: W } = scene
  const lanes = Math.max(1, scene.stations.length)
  const plotH = scene.height - TRACK.top - TRACK.bottom
  const height = plotH / lanes

  scene.stations.forEach((station, i) => {
    const top = TRACK.top + i * height
    if (i % 2 === 1) {
      ctx.fillStyle = rgba(T.floorEdge, 0.45)
      ctx.fillRect(TRACK.left, top, W - TRACK.left - TRACK.right, height)
    }
    ctx.strokeStyle = rgba(T.grid, 0.7)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(TRACK.left, Math.round(top) + 0.5)
    ctx.lineTo(W - TRACK.right, Math.round(top) + 0.5)
    ctx.stroke()

    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.font = '500 11px Inter, system-ui, sans-serif'
    ctx.fillStyle = rgba(T.ink, 0.92)
    ctx.fillText(clip(ctx, station.name ?? 'Unnamed station', TRACK.left - 20),
      TRACK.left - 12, top + height / 2 - 7)
    ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.fillStyle = rgba(T.inkFaint, 1)
    ctx.fillText(`${station.cells} cells · ${station.outstanding} out`,
      TRACK.left - 12, top + height / 2 + 6)
  })
}

function drawGrid(ctx: CanvasRenderingContext2D, scene: TrackScene) {
  const { theme: T, height: H } = scene
  const plotW = scene.width - TRACK.left - TRACK.right
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  for (let s = 0; s <= 1.0001; s += 0.25) {
    const x = Math.round(TRACK.left + s * plotW) + 0.5
    ctx.strokeStyle = rgba(T.grid, s === 1 ? 0.9 : 0.55)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x, TRACK.top)
    ctx.lineTo(x, H - TRACK.bottom)
    ctx.stroke()
    ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.fillStyle = rgba(T.inkFaint, 1)
    ctx.fillText(`${Math.round(s * 100)}%`, x, H - TRACK.bottom + 6)
  }
  ctx.textAlign = 'left'
  ctx.fillStyle = rgba(T.inkFaint, 1)
  ctx.fillText('SHARE OF THE CELL THAT HAS VOTED', TRACK.left, 10)
}

/**
 * The pace rule.
 *
 * Drawn with a soft leading edge in the direction it is travelling, so during a replay it reads
 * as sweeping rather than as jumping between hours.
 */
function drawPace(ctx: CanvasRenderingContext2D, scene: TrackScene) {
  const { theme: T, height: H } = scene
  const plotW = scene.width - TRACK.left - TRACK.right
  const x = TRACK.left + Math.max(0, Math.min(1, scene.paceShare)) * plotW

  ctx.save()
  const wash = ctx.createLinearGradient(x - 46, 0, x, 0)
  wash.addColorStop(0, rgba(T.pace, 0))
  wash.addColorStop(1, rgba(T.pace, scene.replaying ? 0.10 : 0.05))
  ctx.fillStyle = wash
  ctx.fillRect(x - 46, TRACK.top, 46, H - TRACK.top - TRACK.bottom)

  ctx.strokeStyle = rgba(T.pace, 0.75)
  ctx.lineWidth = 1.4
  ctx.beginPath()
  ctx.moveTo(Math.round(x) + 0.5, TRACK.top - 4)
  ctx.lineTo(Math.round(x) + 0.5, H - TRACK.bottom + 2)
  ctx.stroke()

  ctx.font = '500 8.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(T.pace, 0.9)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  ctx.fillText('PACE', x, TRACK.top - 6)
  ctx.restore()
}

function drawMotes(
  ctx: CanvasRenderingContext2D, scene: TrackScene, motes: Map<string, Mote>,
) {
  const { theme: T } = scene

  // Trails first, under everything, in an additive pass so overlapping comets brighten.
  if (scene.showTrails) {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (const cell of scene.cells) {
      const mote = motes.get(cell.id)
      if (!mote || mote.trail.length < 6) continue
      if (scene.missed?.has(cell.id)) continue
      const ink = inkOf(scene, cell, scene.votedAt.get(cell.id) ?? cell.voted)
      for (let i = 0; i < mote.trail.length - 2; i += 2) {
        const t = i / (mote.trail.length - 2)
        ctx.strokeStyle = rgba(ink, 0.05 + t * 0.22)
        ctx.lineWidth = 0.6 + t * 1.6
        ctx.beginPath()
        ctx.moveTo(mote.trail[i] as number, mote.trail[i + 1] as number)
        ctx.lineTo(mote.trail[i + 2] as number, mote.trail[i + 3] as number)
        ctx.stroke()
      }
    }
    ctx.restore()
  }

  for (const cell of scene.cells) {
    const mote = motes.get(cell.id)
    if (!mote) continue
    const votedAt = scene.votedAt.get(cell.id) ?? cell.voted
    const ink = inkOf(scene, cell, votedAt)
    const missed = scene.missed?.has(cell.id) ?? false
    const focused = scene.hovered === cell.id || scene.selected === cell.id
    const alpha = missed ? 0.12 : 1

    ctx.save()

    // An arrival: one ring, expanding and fading. The floor's heartbeat.
    if (mote.flash > 0 && !missed) {
      ctx.strokeStyle = rgba(T.ahead, mote.flash * 0.85)
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(mote.x, mote.y, mote.radius + (1 - mote.flash) * 26, 0, Math.PI * 2)
      ctx.stroke()
    }

    // A stalled cell breathes. Slow, dim, and unmistakably not the arrival ring.
    const stalled = cell.stalledMinutes !== null && cell.stalledMinutes > 90
      && cell.members > votedAt
    if (stalled && !missed) {
      const pulse = 0.5 + 0.5 * Math.sin(scene.now / 620)
      ctx.strokeStyle = rgba(T.inkFaint, 0.20 + pulse * 0.28)
      ctx.setLineDash([2, 3])
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(mote.x, mote.y, mote.radius + 6, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
    }

    ctx.globalCompositeOperation = 'lighter'
    const glow = ctx.createRadialGradient(mote.x, mote.y, 0, mote.x, mote.y, mote.radius * 3.2)
    glow.addColorStop(0, rgba(ink, 0.30 * alpha))
    glow.addColorStop(1, rgba(ink, 0))
    ctx.fillStyle = glow
    ctx.beginPath()
    ctx.arc(mote.x, mote.y, mote.radius * 3.2, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'

    ctx.fillStyle = rgba(ink, 0.9 * alpha)
    ctx.beginPath()
    ctx.arc(mote.x, mote.y, mote.radius, 0, Math.PI * 2)
    ctx.fill()

    // The queue is inside the dot: people who are AT the station and not yet counted. A cell
    // with a full inner ring is not slow, it is waiting, and those need opposite responses.
    if (cell.inQueue > 0 && cell.members > 0 && !missed) {
      const share = Math.min(1, cell.inQueue / cell.members)
      ctx.strokeStyle = rgba(T.queue, 0.95)
      ctx.lineWidth = 1.6
      ctx.beginPath()
      ctx.arc(mote.x, mote.y, mote.radius + 2.4, -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * share)
      ctx.stroke()
    }

    if (cell.escalationsOpen > 0 && !missed) {
      ctx.fillStyle = rgba(T.critical, 0.95)
      ctx.beginPath()
      ctx.arc(mote.x + mote.radius + 4, mote.y - mote.radius - 3, 2, 0, Math.PI * 2)
      ctx.fill()
    }

    if (focused) {
      ctx.strokeStyle = rgba(T.ink, 0.9)
      ctx.lineWidth = 1.2
      ctx.setLineDash([2, 3])
      ctx.beginPath()
      ctx.arc(mote.x, mote.y, mote.radius + 9, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '500 10px ui-monospace, "JetBrains Mono", Menlo, monospace'
      ctx.fillStyle = rgba(T.ink, 1)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.fillText(cell.code, mote.x, mote.y - mote.radius - 12)
    }
    ctx.restore()
  }
}

export function hitMote(motes: Map<string, Mote>, sx: number, sy: number): string | null {
  let best: { id: string, d: number } | null = null
  for (const mote of motes.values()) {
    const d = Math.hypot(mote.x - sx, mote.y - sy)
    if (d <= mote.radius + 7 && (!best || d < best.d)) best = { id: mote.id, d }
  }
  return best?.id ?? null
}

function clip(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text
  let cut = text
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > width) cut = cut.slice(0, -1)
  return `${cut}…`
}

// ── the day strip ────────────────────────────────────────────────────────────

export const STRIP = { left: 148, right: 28, top: 14, bottom: 20 }

/**
 * The day, as a curve you can drag.
 *
 * Cumulative turnout against the pace a linear day would have required, plus the hour's own
 * count as bars. The curve alone cannot show a stall — it goes on rising by nothing — so the
 * bars are what make the midday lull visible, and the lull is the thing an operations room
 * plans the afternoon around.
 */
export function drawStrip(
  ctx: CanvasRenderingContext2D, beats: FloorBeat[], hours: string[], index: number,
  width: number, height: number, dpr: number, theme: Theme, now: number,
) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = rgba(theme.floor, 1)
  ctx.fillRect(0, 0, width, height)
  if (beats.length === 0) return

  const plotW = width - STRIP.left - STRIP.right
  const plotH = height - STRIP.top - STRIP.bottom
  const peak = Math.max(1, ...beats.map((b) => Math.max(b.cumulativeVoted, b.requiredPace)))
  const hourPeak = Math.max(1, ...beats.map((b) => b.votedInHour))
  const x = (i: number) => STRIP.left + (i / Math.max(1, beats.length - 1)) * plotW
  const y = (v: number) => STRIP.top + plotH - (v / peak) * plotH

  // Hour bars — the velocity.
  beats.forEach((beat, i) => {
    const h = (beat.votedInHour / hourPeak) * plotH * 0.5
    const w = Math.max(3, plotW / beats.length - 3)
    ctx.fillStyle = rgba(theme.queue, i <= index ? 0.30 : 0.10)
    ctx.fillRect(x(i) - w / 2, STRIP.top + plotH - h, w, h)
  })

  // The pace a linear day would need — a dashed reference, never a target anybody set.
  ctx.strokeStyle = rgba(theme.inkFaint, 0.7)
  ctx.setLineDash([3, 3])
  ctx.lineWidth = 1
  ctx.beginPath()
  beats.forEach((beat, i) => (i ? ctx.lineTo(x(i), y(beat.requiredPace))
    : ctx.moveTo(x(i), y(beat.requiredPace))))
  ctx.stroke()
  ctx.setLineDash([])

  // Cumulative turnout, filled to the scrubbed hour so the drag has something to grip.
  ctx.beginPath()
  ctx.moveTo(x(0), STRIP.top + plotH)
  beats.forEach((beat, i) => ctx.lineTo(x(i), y(beat.cumulativeVoted)))
  ctx.lineTo(x(beats.length - 1), STRIP.top + plotH)
  ctx.closePath()
  const fill = ctx.createLinearGradient(0, STRIP.top, 0, STRIP.top + plotH)
  fill.addColorStop(0, rgba(theme.ahead, 0.26))
  fill.addColorStop(1, rgba(theme.ahead, 0.02))
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, x(index) + 1, height)
  ctx.clip()
  ctx.fillStyle = fill
  ctx.fill()
  ctx.restore()

  ctx.strokeStyle = rgba(theme.ahead, 0.95)
  ctx.lineWidth = 1.6
  ctx.beginPath()
  beats.forEach((beat, i) => (i ? ctx.lineTo(x(i), y(beat.cumulativeVoted))
    : ctx.moveTo(x(i), y(beat.cumulativeVoted))))
  ctx.stroke()

  // The playhead, with a soft pulse so it is findable on a busy strip.
  const px = Math.round(x(index)) + 0.5
  const pulse = 0.6 + 0.4 * Math.sin(now / 380)
  ctx.strokeStyle = rgba(theme.pace, 0.55 + pulse * 0.4)
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(px, STRIP.top - 6)
  ctx.lineTo(px, STRIP.top + plotH + 4)
  ctx.stroke()
  ctx.fillStyle = rgba(theme.pace, 0.9)
  ctx.beginPath()
  ctx.arc(px, y(beats[index]?.cumulativeVoted ?? 0), 3.4, 0, Math.PI * 2)
  ctx.fill()

  // Hour labels, thinned so they never collide.
  ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  const every = beats.length > 14 ? 2 : 1
  hours.forEach((hour, i) => {
    if (i % every !== 0) return
    ctx.fillStyle = rgba(i === index ? theme.ink : theme.inkFaint, 1)
    ctx.fillText(new Date(hour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      x(i), STRIP.top + plotH + 6)
  })

  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.font = '400 9px ui-monospace, "JetBrains Mono", Menlo, monospace'
  ctx.fillStyle = rgba(theme.inkFaint, 1)
  ctx.fillText('THE DAY', STRIP.left - 12, STRIP.top + plotH / 2)
}

/** Screen x → hour index, for the scrubber. */
export function hourAt(x: number, width: number, count: number): number {
  const plotW = width - STRIP.left - STRIP.right
  const t = (x - STRIP.left) / (plotW || 1)
  return Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))))
}
